import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/** Edge-anchored drawer built on Radix Dialog (focus-trap, Esc, overlay, scroll-lock). */

export const Sheet = DialogPrimitive.Root;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: SheetSide;
  /** Accessible name. Defaults to the navigation drawer's, which is what most callers are. */
  title?: string;
}

type SheetSide = 'left' | 'right' | 'bottom';

/**
 * `bottom` is the phone form factor: full width, capped at most of the viewport so the page
 * behind stays visible, and rounded at the top edge it slides up from. The side drawers keep
 * their fixed width; a bottom sheet that narrow would look like a mistake.
 */
const sideClasses = (side: SheetSide) => {
  if (side === 'bottom') {
    return 'inset-x-0 bottom-0 h-auto max-h-[85vh] w-full rounded-t-xl data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom';
  }
  return side === 'left'
    ? 'inset-y-0 left-0 h-full w-[17rem] max-w-[85vw] data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left'
    : 'inset-y-0 right-0 h-full w-[17rem] max-w-[85vw] data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right';
};

export const SheetContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, SheetContentProps>(
  ({ side = 'left', title, className, children, ...props }, ref) => {
    const { t } = useTranslation();

    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed z-50 flex flex-col overflow-y-auto bg-background shadow-xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out duration-200',
            sideClasses(side),
            className
          )}
          {...props}
        >
          <DialogPrimitive.Title className="sr-only">{title ?? t('common.navigation_menu')}</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  }
);
SheetContent.displayName = 'SheetContent';
